import { __assign, __read, __rest } from "tslib";
import { useCombobox } from 'downshift';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { v1 } from 'uuid';
import { AutocompleteList } from './AutocompleteList';
import { AutocompleteDefaultItem, } from './AutocompleteDefaultItem';
import { Input } from '../Input/Input';
import { useInputPropsMergedWithFormFieldContext } from '../FormField/FormFieldContext/FormFieldContext';
import { FormFieldContextProvider } from '../FormField/FormFieldContext/FormFieldContext';
import { mergeStrings } from '../../utils/strings/strings';
import { extractPropsForInputComponent } from '../internal/extract-props/extractPropsForInputComponent';
import { getCleanObject } from '../../utils/object/cleanupObject';
import { extractAriaAttributes } from '../internal/extract-props/extractAriaAttributes';
import { useCompositeComponentFocusHandlers } from '../internal/useCompositeComponentFocusHandlers/useCompositeComponentFocusHandlers';
export function Autocomplete(incomingProps) {
    var props = useInputPropsMergedWithFormFieldContext(incomingProps);
    var _a = extractPropsForInputComponent(props), rootElementAttributes = _a.rootElementAttributes, baseInputComponentProps = _a.baseInputComponentProps, baseHtmlInputAttributes = _a.baseHtmlInputAttributes, ariaAttributes = _a.ariaAttributes, dataAttributes = _a.dataAttributes, restProps = _a.restProps;
    var id = rootElementAttributes.id, rootAttrs = __rest(rootElementAttributes, ["id"]);
    var labelId = restProps.labelId, inputValue = restProps.inputValue, _b = restProps.defaultHighlightFirstItem, defaultHighlightFirstItem = _b === void 0 ? true : _b, initialInputValue = restProps.initialInputValue, initialSelectedItem = restProps.initialSelectedItem, items = restProps.items, onSelect = restProps.onSelect, onInputChange = restProps.onInputChange, onFocus = restProps.onFocus, onBlur = restProps.onBlur, name = restProps.name, placeholder = restProps.placeholder, selectedItemProp = restProps.selectedItem, _c = restProps.itemToString, itemToString = _c === void 0 ? defaultItemToString : _c, _d = restProps.itemToDefaultItem, itemToDefaultItem = _d === void 0 ? defaultItemToDefaultItem : _d, _e = restProps.ItemComponent, ItemComponent = _e === void 0 ? AutocompleteDefaultItem : _e, openProp = restProps.open;
    // taking out the aria-describedby that might come from FormField,
    // we want it on the root in this case.
    var ariaDescribedby = (baseInputComponentProps.inputProps || {})["aria-describedby"];
    var readyToShowItems = useReadyToShowItems(openProp);
    var inputComponentRef = useRef(null);
    var usesDefaultItemComponent = ItemComponent === AutocompleteDefaultItem;
    var randomAutoCompleteName = useMemo(function () { return '__ignore-me_' + v1(); }, []);
    var selectedItemChangeHandler = useCallback(function (changes) {
        onSelect === null || onSelect === void 0 ? void 0 : onSelect({
            selectedItem: changes.selectedItem,
            inputValue: itemToString(changes.selectedItem),
            name: name,
        });
    }, [onSelect, name]);
    var inputChangeHandler = useCallback(function (changes) {
        if (changes.type === useCombobox.stateChangeTypes.InputChange) {
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange({
                value: changes.inputValue,
                name: name,
            });
        }
    }, [onInputChange, name]);
    /* This state change handler is to let the use select the already selected item.
     * Downshift does not trigger an [onSelectedItemChange] if the item clicked is the
     * already selected item, and that lease the user with a closed list, but
     * still an input that does not reflect the selected item. */
    var stateChangeHandler = useCallback(function (changes) {
        if (changes.type === useCombobox.stateChangeTypes.ItemClick &&
            changes.selectedItem === undefined &&
            selectedItemProp) {
            onSelect === null || onSelect === void 0 ? void 0 : onSelect({
                selectedItem: selectedItemProp,
                inputValue: itemToString(selectedItemProp),
            });
        }
    }, [selectedItemProp]);
    /* todo there is a bug in Downshift useComboBox: https://github.com/downshift-js/downshift/issues/1232
     * https://leaseplan-digital.atlassian.net/browse/VEL-830
     * therefore, using getCleanObject to filter out all undefined values. */
    var _f = useCombobox(getCleanObject({
        id: id,
        labelId: labelId,
        items: items,
        selectedItem: selectedItemProp,
        itemToString: itemToString,
        inputValue: inputValue,
        initialInputValue: initialInputValue,
        initialSelectedItem: initialSelectedItem,
        isOpen: openProp,
        onSelectedItemChange: selectedItemChangeHandler,
        onInputValueChange: inputChangeHandler,
        onStateChange: stateChangeHandler,
        defaultHighlightedIndex: defaultHighlightFirstItem ? 0 : undefined,
    })), getInputProps = _f.getInputProps, getMenuProps = _f.getMenuProps, getComboboxProps = _f.getComboboxProps, isOpen = _f.isOpen, getItemProps = _f.getItemProps, highlightedIndex = _f.highlightedIndex, selectedItem = _f.selectedItem;
    var rootCompositeFocusProps = useCompositeComponentFocusHandlers({
        onFocus: onFocus,
        onBlur: onBlur,
        name: name,
        blurForNonFocusableElementsWithin: false,
    }).rootCompositeFocusProps;
    var _g = getInputProps(), inputRef = _g.ref, allDownshiftInputProps = __rest(_g, ["ref"]);
    var _h = extractAriaAttributes(allDownshiftInputProps), inputAriaAttrs = _h.ariaAttributes, rootInputProps = _h.restProps;
    // surpressedRefError because when the Autocomplete menu is rendered, it's not open,
    // which will lead to a ref error it not surpressed.
    var menuProps = getMenuProps({}, { suppressRefError: true });
    var comboboxProps = getComboboxProps();
    var selectedItemValue = selectedItem ? itemToString(selectedItem) : null;
    return (React.createElement("div", __assign({}, rootAttrs, ariaAttributes, dataAttributes, comboboxProps, rootCompositeFocusProps, { "aria-describedby": mergeStrings([
            ariaAttributes === null || ariaAttributes === void 0 ? void 0 : ariaAttributes['aria-describedby'],
            ariaDescribedby,
        ]), id: id }),
        React.createElement(FormFieldContextProvider, { value: undefined },
            React.createElement(Input, __assign({}, baseInputComponentProps, baseHtmlInputAttributes, rootInputProps, { autoComplete: randomAutoCompleteName, placeholder: placeholder, name: name, inputProps: __assign(__assign(__assign({}, baseInputComponentProps.inputProps), inputAriaAttrs), { 'aria-describedby': undefined }), inputRef: inputRef, ref: inputComponentRef }))),
        readyToShowItems && (React.createElement(AutocompleteList, __assign({}, menuProps, { anchorEl: inputComponentRef.current, open: isOpen && items.length > 0, "aria-labelledby": undefined }), items.map(function (item, index) {
            var suggestionValue = itemToString(item);
            var _item = usesDefaultItemComponent
                ? itemToDefaultItem(item)
                : item;
            return (React.createElement(ItemComponent, { key: suggestionValue + index, highlighted: highlightedIndex === index, selected: selectedItemValue === suggestionValue, item: _item, attributes: getItemProps({ item: item, index: index }) }));
        })))));
}
var defaultItemToDefaultItem = function (item) {
    return {
        label: typeof item === 'string' ? item : item.label,
        subLabel: typeof item === 'string' ? undefined : item.subLabel,
    };
};
var defaultItemToString = function (item) {
    if (!item) {
        return '';
    }
    else if (typeof item === 'string') {
        return item;
    }
    else {
        return item.label;
    }
};
/* Doing this because when open is true, the AutocompleteMenu
 * is shown, but there is no inputComponentRef set yet. */
function useReadyToShowItems(open) {
    var _a = __read(useState(!open), 2), ready = _a[0], setReady = _a[1];
    useEffect(function () {
        setReady(true);
    }, []);
    return ready;
}
//# sourceMappingURL=Autocomplete.js.map